import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { ToastService } from '@services/toast.service';

@Component({
  selector: 'app-toast-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="toast-container">
      @for (toast of toastService.activeToasts(); track toast.id) {
        <div
          class="toast"
          [class.toast-success]="toast.type === 'success'"
          [class.toast-error]="toast.type === 'error'"
          [class.toast-info]="toast.type === 'info'"
          [class.toast-warning]="toast.type === 'warning'"
        >
          <div class="toast-content">
            <i
              class="toast-icon fas"
              [class.fa-check-circle]="toast.type === 'success'"
              [class.fa-exclamation-circle]="toast.type === 'error'"
              [class.fa-info-circle]="toast.type === 'info'"
              [class.fa-exclamation-triangle]="toast.type === 'warning'"
            >
            </i>
            <span class="toast-message">{{ toast.message }}</span>
            <button type="button" class="toast-close" (click)="toastService.dismiss(toast.id)">
              <i class="fas fa-times"></i>
            </button>
          </div>
          @if (toast.duration && toast.duration > 0) {
            <div class="toast-progress">
              <div class="toast-progress-bar" [style.width.%]="toast.progress || 0"></div>
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      .toast-container {
        position: fixed;
        bottom: var(--space-lg);
        right: var(--space-lg);
        z-index: 1000;
        display: flex;
        flex-direction: column;
        gap: var(--space-md);
        max-width: 400px;
      }

      .toast {
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-lg);
        animation: slideIn 0.3s ease-out;
        min-width: 320px;
        max-width: 100%;
        overflow: hidden;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
      }

      .toast-success {
        background: linear-gradient(135deg, var(--green-500), var(--green-600));
        border-color: var(--green-400);
      }

      .toast-error {
        background: linear-gradient(135deg, var(--red-500), var(--red-600));
        border-color: var(--red-400);
      }

      .toast-info {
        background: linear-gradient(135deg, var(--blue-500), var(--blue-600));
        border-color: var(--blue-400);
      }

      .toast-warning {
        background: linear-gradient(135deg, var(--orange-500), var(--orange-600));
        border-color: var(--orange-400);
      }

      .toast-content {
        display: flex;
        align-items: center;
        padding: var(--space-lg);
        gap: var(--space-md);
      }

      .toast-icon {
        font-size: var(--text-xl);
        flex-shrink: 0;
        color: var(--text-inverse);
      }

      .toast-message {
        flex: 1;
        color: var(--text-inverse);
        font-size: var(--text-sm);
        line-height: var(--leading-relaxed);
        font-weight: var(--font-medium);
      }

      .toast-close {
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.8);
        cursor: pointer;
        padding: var(--space-xs);
        border-radius: var(--radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: all 0.2s ease;
        font-size: var(--text-sm);
      }

      .toast-close:hover {
        background: rgba(255, 255, 255, 0.2);
        color: var(--text-inverse);
        transform: scale(1.1);
      }

      .toast-progress {
        height: 4px;
        background: rgba(255, 255, 255, 0.2);
        overflow: hidden;
      }

      .toast-progress-bar {
        height: 100%;
        background: rgba(255, 255, 255, 0.8);
        transition: width 0.1s linear;
        border-radius: 0 var(--radius-xs) var(--radius-xs) 0;
      }

      @keyframes slideIn {
        from {
          transform: translateY(100%) translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0) translateX(0);
          opacity: 1;
        }
      }

      @media (max-width: 768px) {
        .toast-container {
          bottom: var(--space-md);
          right: var(--space-md);
          left: var(--space-md);
          max-width: none;
        }

        .toast {
          min-width: 0;
        }
      }

      @media (max-width: 480px) {
        .toast-container {
          bottom: var(--space-sm);
          right: var(--space-sm);
          left: var(--space-sm);
        }

        .toast-content {
          padding: var(--space-md);
          gap: var(--space-sm);
        }

        .toast-message {
          font-size: var(--text-xs);
        }
      }

      /* Prevent body scroll when multiple toasts */
      :host {
        pointer-events: none;
      }

      .toast {
        pointer-events: auto;
      }

      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        .toast {
          animation: none;
        }

        .toast-close {
          transition: none;
        }

        .toast-close:hover {
          transform: none;
        }
      }

      /* High contrast mode support */
      @media (prefers-contrast: high) {
        .toast {
          border-width: 2px;
        }
      }
    `,
  ],
})
export class ToastComponent {
  toastService = inject(ToastService);
}
